import React from 'react';

// ─── Signal colours ───────────────────────────────────────────
const sigColour = { BULLISH: '#00ff88', BEARISH: '#ff4444', NEUTRAL: '#ffaa00' };
const sigBg     = { BULLISH: 'rgba(0,255,136,0.08)', BEARISH: 'rgba(255,68,68,0.08)', NEUTRAL: 'rgba(255,170,0,0.08)' };
const sigIcon   = { BULLISH: '▲', BEARISH: '▼', NEUTRAL: '◆' };

// ─── Blurred locked row ───────────────────────────────────────
const LockedRow = ({ label }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
    <span style={{ fontSize: '0.8rem', color: '#475569' }}>{label}</span>
    <span style={{ fontSize: '0.8rem', color: '#1e293b', background: '#1e293b', borderRadius: '4px', userSelect: 'none', filter: 'blur(4px)' }}>████████</span>
  </div>
);

// ─── Ad Unit placeholder ──────────────────────────────────────
const AdUnit = () => (
  <div style={{ margin: '1.5rem 0', padding: '1rem', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '8px', textAlign: 'center' }}>
    <p style={{ fontSize: '0.65rem', color: '#334155', margin: '0 0 0.5rem' }}>ADVERTISEMENT</p>
    <div style={{ height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: '0.75rem', color: '#1e293b' }}>Ad loading...</span>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────
const AnalysisPreview = ({ analysis, setShowAuth, setAuthMode }) => {
  if (!analysis) return null;

  const { ticker, companyName, recommendation, confidence, score, currentPrice, priceChange, guestRemaining } = analysis;
  const rec     = recommendation || 'NEUTRAL';
  const colour  = sigColour[rec] || '#ffaa00';
  const bg      = sigBg[rec]     || 'rgba(255,170,0,0.08)';
  const icon    = sigIcon[rec]   || '◆';
  const change  = priceChange?.change        || 0;
  const changePct = priceChange?.changePercent || 0;

  const handleLogin = () => { setAuthMode('login'); setShowAuth(true); };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', fontFamily: '"DM Sans", system-ui, sans-serif' }}>

      {/* Guest banner */}
      <div style={{ marginBottom: '1rem', padding: '0.75rem 1.25rem', background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
          👁️ Guest preview — {guestRemaining ?? 0} free {guestRemaining === 1 ? 'analysis' : 'analyses'} remaining this hour
        </span>
        <button onClick={handleLogin}
          style={{ padding: '0.35rem 1rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '6px', color: '#050d1a', fontWeight: 800, fontSize: '0.78rem', cursor: 'pointer' }}>
          Login for Full Access
        </button>
      </div>

      {/* Main card */}
      <div style={{ background: 'rgba(10,20,40,0.8)', border: '1px solid rgba(0,212,255,0.12)', borderRadius: '16px', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Stock Analysis</div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: '#f8fafc', margin: '0 0 0.2rem' }}>{ticker}</h2>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>{companyName}</p>
          </div>

          {/* Signal badge */}
          <div style={{ padding: '0.75rem 1.5rem', background: bg, border: `1px solid ${colour}30`, borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', marginBottom: '0.2rem' }}>{icon}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: colour, letterSpacing: '0.05em' }}>{rec}</div>
            <div style={{ fontSize: '0.65rem', color: colour + '99', fontWeight: 700 }}>{confidence} CONFIDENCE</div>
          </div>
        </div>

        {/* Price row — visible */}
        <div style={{ padding: '1.25rem 1.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Current Price</div>
            <div style={{ fontSize: '2rem', fontWeight: 900, color: '#f8fafc' }}>${currentPrice?.toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Today</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: change >= 0 ? '#00ff88' : '#ff4444' }}>
              {change >= 0 ? '+' : ''}{change?.toFixed(2)} ({changePct >= 0 ? '+' : ''}{changePct?.toFixed(2)}%)
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Score</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: colour }}>{score}/100</div>
          </div>
        </div>

        {/* Locked fundamentals */}
        <div style={{ padding: '1.25rem 1.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Fundamentals</div>
          <LockedRow label="P/E Ratio" />
          <LockedRow label="EPS (TTM)" />
          <LockedRow label="Market Cap" />
          <LockedRow label="52W High / Low" />
          <LockedRow label="Revenue (TTM)" />
        </div>

        {/* Ad unit */}
        <div style={{ padding: '0 1.75rem' }}>
          <AdUnit />
        </div>

        {/* Locked analysis text */}
        <div style={{ padding: '1.25rem 1.75rem', position: 'relative' }}>
          <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Algorithmic Analysis</div>
          <div style={{ filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', color: '#64748b', fontSize: '0.9rem', lineHeight: 1.75 }}>
            This stock shows strong momentum indicators with consistent volume patterns. The technical setup suggests a potential breakout above key resistance levels. Fundamental metrics indicate solid earnings growth with improving profit margins over recent quarters.
          </div>

          {/* Unlock overlay */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,13,30,0.7)', backdropFilter: 'blur(2px)', borderRadius: '0 0 16px 16px' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔒</div>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '1rem', textAlign: 'center' }}>Full analysis available to members</p>
            <button onClick={handleLogin}
              style={{ padding: '0.65rem 1.75rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '8px', color: '#050d1a', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>
              Login for Full Analysis
            </button>
          </div>
        </div>

      </div>

      {/* Disclaimer */}
      <p style={{ fontSize: '0.65rem', color: '#334155', textAlign: 'center', marginTop: '1rem', lineHeight: 1.6 }}>
        ⚠️ Algorithmic output only. Not investment advice. Data delayed 15 minutes. For informational purposes only.
      </p>
    </div>
  );
};

export default AnalysisPreview;
